import {
	createVestingTermGraded,
	deleteVestingTermGraded,
	updateVestingTermGraded,
	UpdateVestingTermGradedArgs,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	createOrganizationVestingTermGraded,
	deleteOrganizationVestingTermGraded,
	updateOrganizationVestingTermGraded,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults-vesting.service';
import {
	CreateVestingTermGradedDto,
	UpdateVestingTermGradedDto,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

export const useGradedMutations = (
	refetchVestingTerms: () => Promise<unknown>,
	organizationId?: string
) => {
	const createVestingTermGradedRequest = useMutation({
		mutationFn: (args: CreateVestingTermGradedDto) =>
			organizationId
				? createOrganizationVestingTermGraded(organizationId, args)
				: createVestingTermGraded(args),
	});
	const updateVestingTermGradedRequest = useMutation({
		mutationFn: (args: UpdateVestingTermGradedArgs) =>
			organizationId
				? updateOrganizationVestingTermGraded(organizationId, args)
				: updateVestingTermGraded(args),
	});
	const deleteVestingTermGradedRequest = useMutation({
		mutationFn: (args: { gradedTermId: string }) =>
			organizationId
				? deleteOrganizationVestingTermGraded(organizationId, args)
				: deleteVestingTermGraded(args),
	});

	const onCreateVestingTermGraded = useCallback(
		(dto: CreateVestingTermGradedDto) => {
			createVestingTermGradedRequest.mutate(dto, {
				onSuccess: async () => {
					await refetchVestingTerms();
				},
			});
		},
		[createVestingTermGradedRequest, refetchVestingTerms]
	);

	const onUpdateVestingTermGraded = useCallback(
		(gradedTermId: string, formValues: UpdateVestingTermGradedDto) => {
			updateVestingTermGradedRequest.mutate(
				{ gradedTermId, ...formValues },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[updateVestingTermGradedRequest, refetchVestingTerms]
	);

	const onDeleteVestingTermGraded = useCallback(
		(gradedTermId: string) => {
			deleteVestingTermGradedRequest.mutate(
				{ gradedTermId },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[deleteVestingTermGradedRequest, refetchVestingTerms]
	);

	return useMemo(
		() => ({
			onCreateVestingTermGraded,
			onUpdateVestingTermGraded,
			onDeleteVestingTermGraded,
		}),
		[onCreateVestingTermGraded, onUpdateVestingTermGraded, onDeleteVestingTermGraded]
	);
};
